import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { memo, useState, useEffect } from "react";
import axios from "axios";

/* ========= CUSTOM COMPONENTS ========= */
import GlobalDropdownField from "../../../../shared/utils/GlobalDropdownField";
import CustomInput from "../../../../shared/utils/Input";
import { statecityData } from "../../../../shared/enums";
import Swal from "sweetalert2";
import GlobalButton from "../../../../shared/utils/GlobalButton";

/* ========= CONSTANTS ========= */

/* ========= COMPONENT ========= */
const API_BASE_URL = process.env.REACT_APP_URL;
const cityApiKey = process.env.REACT_APP_FETCH_STATE_CITY_API_KEY;

const AddJobDialog = ({
  open,
  onClose,
  campaignId,
  soDetails,
  selectedJob,
  categoryData,
  productDetails,
  fetchAllJobsBasedOnCampaignId,
  createJobFrom,
  fetchAllBatchesBasedOnCampaignId,
  setAnchorEl,
  jobForWhichService,
  onJobCreated,
  showError,
}) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    storeName: selectedJob ? selectedJob.storeName : "",
    ownerName: selectedJob?.storeOwnerName ?? "",
    ownerPhone: selectedJob?.storeOwnerPhoneNumber ?? "",
    address: selectedJob?.address ?? "",
    state: selectedJob?.state ?? "",
    city: selectedJob?.city ?? "",
    pincode: selectedJob?.zipCode ?? "",
    soName: "",
    soPhone: "",
    products: [
      {
        productType: "",
        width: "",
        height: "",
        thickness: "",
      },
    ],
  });
  const [formErrors, setFormErrors] = useState({});
  const [cityArray, setCityArray] = useState([]);

  //duplicating the job in supply section inside the batch
  const isPaintingDuplicate =
    createJobFrom === "batches" && jobForWhichService === "Painting";
  const selectedProductType = selectedJob?.product?.productType || "";

  useEffect(() => {
    if (isPaintingDuplicate && selectedJob && selectedProductType) {
      setFormData((prev) => ({
        ...prev,
        products: [
          {
            productType: selectedProductType,
            width:
              selectedJob?.product?.approvedWidth ||
              selectedJob?.product?.productWidth ||
              "",
            height:
              selectedJob?.product?.approvedHeight ||
              selectedJob?.product?.productHeight ||
              "",
            thickness: selectedJob?.product?.productThickness || "",
          },
        ],
      }));
    }
  }, [isPaintingDuplicate, selectedJob, selectedProductType]);

  /* ========= HANDLERS ========= */

  useEffect(() => {
    if (selectedJob) {
      setFormData({
        storeName: selectedJob.storeName || "",
        ownerName: selectedJob.storeOwnerName || "",
        ownerPhone: selectedJob.storeOwnerPhoneNumber || "",
        address: selectedJob.address || "",
        state: selectedJob.state || "",
        city: selectedJob.city || "",
        pincode: selectedJob.zipCode || "",
        installationPartnerId: selectedJob.installationPartnerId?._id || "",
        soName: "",
        soPhone: "",
        products: [
          {
            productType: isPaintingDuplicate ? selectedProductType : "",
            width: "",
            height: "",
            thickness: "",
          },
        ],
      });
    }
  }, [selectedJob]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "ownerPhone" && value.length > 10) return; // limit phone number to 10 digits
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, valueOrEvent) => {
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent?.target?.value;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" && { city: "" }),
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...formData.products];

    updated[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      products: updated,
    }));
  };

  const addProduct = () => {
    setFormData((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          productType: isPaintingDuplicate ? selectedProductType || "" : "",
          width: "",
          height: "",
          thickness: "",
        },
      ],
    }));
  };

  const removeProduct = (index) => {
    const updated = [...formData.products];
    updated.splice(index, 1);
    setFormData((prev) => ({ ...prev, products: updated }));
  };

  /* ========= SUBMIT ========= */

  const handleSubmit = async () => {
    const errors = {};
    const {
      storeName,
      ownerName,
      ownerPhone,
      address,
      state,
      city,
      pincode,
      soName,
      soPhone,
      products,
    } = formData;

    if (!storeName) errors.storeName = "Store Name is required";
    if (!ownerName) errors.ownerName = "Owner Name is required";
    if (!ownerPhone) errors.ownerPhone = "Owner Phone is required";
    else if (!/^\d{10}$/.test(ownerPhone))
      errors.ownerPhone = "Must be 10 digits";
    if (!address) errors.address = "Address is required";
    if (!state) errors.state = "State is required";
    if (!city) errors.city = "City is required";
    if (!pincode) errors.pincode = "Pincode is required";

    products.forEach((p, idx) => {
      if (!p.productType) errors[`productType_${idx}`] = "Select Product Type";
      if (!p.width) errors[`width_${idx}`] = "Width required";
      if (!p.height) errors[`height_${idx}`] = "Height required";
      if (!p.thickness) errors[`thickness_${idx}`] = "Thickness required";
    });

    if (!soName) errors.soName = "Select SO";
    if (!soPhone) errors.soPhone = "SO Phone missing";

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return; // stop submission if errors

    // Submit to backend
    const token = localStorage.getItem("token");
    try {
      setLoading(true);
      const payload = {
        storeName,
        storeOwnerName: ownerName,
        storeOwnerPhoneNumber: Number(ownerPhone),
        address,
        city,
        state,
        zipCode: pincode,
        products: products?.map((p) => ({
          productType: p.productType,
          productWidth: Number(p.width),
          productHeight: Number(p.height),
          productThickness: Number(p.thickness),
        })),
        soName,
        soPhoneNumber: Number(soPhone),
        campaignId,
      };

      let response;

      if (jobForWhichService === "Recce") {
        if (selectedJob) {
          payload.madeByAdmin = true;
          payload.batchOfJobsForInstallationPartnerId =
            selectedJob?.batchOfJobsForInstallationPartnerId;
        }
        if (selectedJob?.installationPartnerId) {
          payload.installationPartnerId =
            selectedJob?.installationPartnerId?._id;
        }
      } else if (jobForWhichService === "Painting") {
        if (selectedJob) {
          payload.madeByAdmin = true;
          payload.batchOfJobsForVendorId = selectedJob?.batchOfJobsForVendorId;
        }
      }

      if (jobForWhichService === "Recce") {
        response = await axios.post(
          `${API_BASE_URL}/jobs/createManualJob`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      } else if (jobForWhichService === "Painting") {
        response = await axios.post(
          `${API_BASE_URL}/manufacturingJobs/createPaintingJob`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      // Get the created job data from response
      const createdJob = response?.data?.data;

      // Use callback instead of direct fetch for batch duplication
      if (createJobFrom === "batches") {
        if (onJobCreated) {
          onJobCreated(createdJob, selectedJob?._id);
        }
        if (setAnchorEl) {
          setAnchorEl(null);
        }
      } else {
        if (fetchAllJobsBasedOnCampaignId) {
          fetchAllJobsBasedOnCampaignId(id);
        }
      }

      onClose();
    } catch (error) {
      const errMsg =
        error?.response?.data?.error || error.message || "Something went wrong";

      if (showError) {
        showError(errMsg);
      }
      console.error("Job creation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stateCode = statecityData.find(
      (ele) => ele.name === formData.state,
    )?.iso2;

    if (formData.state) {
      const fetchCities = async (stateCode) => {
        const headers = new Headers();
        headers.append("X-CSCAPI-KEY", cityApiKey);

        const requestOptions = {
          method: "GET",
          headers: headers,
          redirect: "follow",
        };

        try {
          const response = await fetch(
            `https://api.countrystatecity.in/v1/countries/IN/states/${stateCode}/cities`,
            requestOptions,
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const cityData = await response.json();
          setCityArray(cityData);

          // Log the city names
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      };

      // Example usage: Fetch cities in Maharashtra (state code "MH")
      fetchCities(stateCode);
    }
  }, [formData.state]);

  /* ========= UI ========= */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Job</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Store Details */}
          <Grid item xs={12} sm={6}>
            <CustomInput
              name="storeName"
              size="small"
              title="Store Name"
              placeholder="Enter Store Name"
              value={formData.storeName}
              onChange={handleChange}
              error={!!formErrors.storeName}
              helperText={formErrors.storeName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomInput
              name="ownerName"
              size="small"
              title="Owner Name"
              placeholder="Enter Owner Name"
              value={formData.ownerName}
              onChange={handleChange}
              error={!!formErrors.ownerName}
              helperText={formErrors.ownerName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomInput
              name="ownerPhone"
              size="small"
              type="number"
              title="Owner Phone"
              placeholder="Enter Phone Number"
              value={formData.ownerPhone}
              onChange={handleChange}
              inputProps={{ maxLength: 10 }}
              error={!!formErrors.ownerPhone}
              helperText={formErrors.ownerPhone}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomInput
              name="address"
              size="small"
              title="Store Address"
              placeholder="Enter Full Address"
              value={formData.address}
              multiline
              rows={2}
              onChange={handleChange}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12} sm={4}>
            <GlobalDropdownField
              label="State"
              name="state"
              value={formData.state}
              options={statecityData?.map((state) => ({
                label: state.name,
                value: state.name,
              }))}
              onChange={(val) => handleDropdownChange("state", val)}
              error={!!formErrors.state}
              helperText={formErrors.state}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <GlobalDropdownField
              name="city"
              label="City"
              value={formData.city}
              options={cityArray?.map((city) => ({
                label: city.name,
                value: city.name,
              }))}
              placeholder="Select City"
              disabled={!formData.state}
              onChange={(value) => handleDropdownChange("city", value)}
              error={!!formErrors.city}
              helperText={formErrors.city}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <CustomInput
              name="pincode"
              type="number"
              title="Pincode"
              placeholder="Enter Pincode"
              value={formData.pincode}
              // onChange={handleChange}
              onChange={(e) => {
                if (
                  !e.target.value.startsWith(" ") &&
                  e.target.value.length < 7
                ) {
                  handleChange(e);
                }
              }}
              InputProps={{
                inputProps: {
                  min: 0,
                  maxLength: 6,
                },
              }}
              size="small"
              error={!!formErrors.pincode}
              helperText={formErrors.pincode}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Products */}
          <Grid item xs={12}>
            <strong>Products</strong>
          </Grid>

          {formData.products?.map((product, index) => (
            <Grid container spacing={2} key={index} pl={2}>
              <Grid item xs={12} sm={6}>
                <GlobalDropdownField
                  label="Product Type"
                  value={product.productType}
                  disabled={isPaintingDuplicate}
                  options={
                    productDetails?.map((p) => ({
                      label: p,
                      value: p,
                    })) || []
                  }
                  placeholder="Select Product"
                  onChange={(val) =>
                    handleProductChange(
                      index,
                      "productType",
                      typeof val === "string" ? val : val?.target?.value,
                    )
                  }
                  error={!!formErrors[`productType_${index}`]}
                  helperText={formErrors[`productType_${index}`]}
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <CustomInput
                  size="small"
                  title="Width"
                  type="number"
                  value={product.width}
                  onChange={(e) =>
                    handleProductChange(index, "width", e.target.value)
                  }
                  InputProps={{
                    inputProps: {
                      min: 0,
                      maxLength: 3,
                    },
                  }}
                  error={!!formErrors[`width_${index}`]}
                  helperText={formErrors[`width_${index}`]}
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <CustomInput
                  title="Height"
                  type="number"
                  size="small"
                  value={product.height}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      maxLength: 3,
                    },
                  }}
                  onChange={(e) =>
                    handleProductChange(index, "height", e.target.value)
                  }
                  error={!!formErrors[`height_${index}`]}
                  helperText={formErrors[`height_${index}`]}
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <CustomInput
                  title="Thickness"
                  type="number"
                  size="small"
                  value={product.thickness}
                  onChange={(e) =>
                    handleProductChange(index, "thickness", e.target.value)
                  }
                  InputProps={{
                    inputProps: {
                      min: 0,
                      maxLength: 3,
                    },
                  }}
                  error={!!formErrors[`thickness_${index}`]}
                  helperText={formErrors[`thickness_${index}`]}
                />
              </Grid>

              {formData.products.length > 1 && (
                <Grid item xs={3}>
                  <GlobalButton
                    variant="warning"
                    size="small"
                    onClick={() => removeProduct(index)}
                  >
                    {`Remove Product`}
                  </GlobalButton>
                </Grid>
              )}
            </Grid>
          ))}

          <Grid item xs={3}>
            <GlobalButton
              variant="secondary"
              size="medium"
              onClick={() => addProduct()}
            >
              {`+ Add Another Product`}
            </GlobalButton>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* SO Details */}
          <Grid item xs={12} sm={6}>
            <GlobalDropdownField
              label="SO Name"
              placeholder="Select SO"
              value={formData.soName}
              options={
                soDetails?.map((so) => ({
                  label: so?.name,
                  value: so?.name,
                })) || []
              }
              onChange={(val) => {
                const selectedSO = soDetails?.find(
                  (so) => so.name === val || so.name === val?.target?.value,
                );
                if (selectedSO) {
                  setFormData((prev) => ({
                    ...prev,
                    soName: selectedSO.name,
                    soPhone: selectedSO.phoneNumber,
                  }));
                }
              }}
              error={!!formErrors.soName}
              helperText={formErrors.soName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomInput
              name="soPhone"
              title="SO Phone"
              size="small"
              value={formData.soPhone}
              disabled
              error={!!formErrors.soPhone}
              helperText={formErrors.soPhone}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px", gap: 2 }}>
        <div style={{ width: "100px" }}>
          <GlobalButton
            variant="secondary"
            size="medium"
            onClick={onClose}
            style={{ width: "100px" }}
          >
            {`Cancel`}
          </GlobalButton>
        </div>
        <div style={{ width: "100px" }}>
          <GlobalButton
            variant="primary"
            size="medium"
            isLoading={loading}
            onClick={() => handleSubmit()}
          >
            {`Submit`}
          </GlobalButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddJobDialog;
